import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Image,
  ArrowUpward,
  ArrowDownward,
  CloudUpload,
  Close
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bannerService } from '../../services/bannerService';

const BannerManagement = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    order_position: 1,
    is_active: true
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const queryClient = useQueryClient();

  // Buscar banners
  const { data: bannersData, isLoading } = useQuery({
    queryKey: ['banners'],
    queryFn: () => bannerService.getAllBanners(),
  });

  const banners = Array.isArray(bannersData?.data?.banners)
    ? bannersData.data.banners
    : Array.isArray(bannersData?.banners)
    ? bannersData.banners
    : Array.isArray(bannersData?.data)
    ? bannersData.data
    : [];

  // Mutations
  const createBannerMutation = useMutation({
    mutationFn: (data) => bannerService.createBanner(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['banners']);
      setSnackbar({ open: true, message: 'Banner criado com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao criar banner', severity: 'error' });
    }
  });

  const updateBannerMutation = useMutation({
    mutationFn: ({ id, data }) => bannerService.updateBanner(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['banners']);
      setSnackbar({ open: true, message: 'Banner atualizado com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao atualizar banner', severity: 'error' });
    }
  });

  const deleteBannerMutation = useMutation({
    mutationFn: (id) => bannerService.deleteBanner(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['banners']);
      setSnackbar({ open: true, message: 'Banner deletado com sucesso!', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao deletar banner', severity: 'error' });
    }
  });

  const reorderBannerMutation = useMutation({
    mutationFn: ({ id, order }) => bannerService.reorderBanner(id, order),
    onSuccess: () => {
      queryClient.invalidateQueries(['banners']);
      setSnackbar({ open: true, message: 'Ordem do banner atualizada!', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao reordenar banner', severity: 'error' });
    }
  });

  const handleOpenDialog = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setFormData({
        title: banner.title || '',
        description: banner.description || '',
        image_url: banner.image_url || banner.imageUrl || '',
        link_url: banner.link_url || banner.linkUrl || '',
        order_position: banner.order_position ?? banner.orderPosition ?? 1,
        is_active: banner.is_active !== undefined ? banner.is_active : (banner.isActive !== undefined ? banner.isActive : true)
      });
      setPreviewUrl(banner.image_url || banner.imageUrl || '');
    } else {
      setEditingBanner(null);
      setFormData({
        title: '',
        description: '',
        image_url: '',
        link_url: '',
        order_position: banners.length + 1,
        is_active: true
      });
      setPreviewUrl('');
    }
    setSelectedFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBanner(null);
    setFormData({
      title: '',
      description: '',
      image_url: '',
      link_url: '',
      order_position: 1,
      is_active: true
    });
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const handleSubmit = () => {
    if (!formData.title || !formData.title.trim()) {
      setSnackbar({ open: true, message: 'O título do banner é obrigatório', severity: 'error' });
      return;
    }

    if (!selectedFile && !formData.image_url) {
      setSnackbar({ open: true, message: 'Selecione uma imagem ou informe a URL da imagem', severity: 'error' });
      return;
    }

    let dataToSend;
    
    // Se há um arquivo selecionado, criar FormData
    if (selectedFile) {
      const formDataToSend = new FormData();
      formDataToSend.append('banner_image', selectedFile);
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('link_url', formData.link_url || '');
      formDataToSend.append('linkUrl', formData.link_url || '');
      formDataToSend.append('is_active', String(formData.is_active));
      formDataToSend.append('isActive', String(formData.is_active));
      formDataToSend.append('order_position', String(formData.order_position));
      formDataToSend.append('orderPosition', String(formData.order_position));
      dataToSend = formDataToSend;
    } else {
      dataToSend = {
        title: formData.title.trim(),
        description: formData.description || '',
        image_url: formData.image_url,
        imageUrl: formData.image_url,
        link_url: formData.link_url || '',
        linkUrl: formData.link_url || '',
        order_position: formData.order_position,
        orderPosition: formData.order_position,
        is_active: formData.is_active,
        isActive: formData.is_active
      };
    }
    
    if (editingBanner) {
      const bannerId = editingBanner.id || editingBanner._id;
      if (!bannerId) {
        setSnackbar({ open: true, message: 'ID do banner não encontrado para atualização', severity: 'error' });
        return;
      }
      updateBannerMutation.mutate({ id: bannerId, data: dataToSend });
    } else {
      createBannerMutation.mutate(dataToSend);
    }
  };

  const handleDelete = (id) => {
    if (!id || id === 'undefined') {
      setSnackbar({ open: true, message: 'ID do banner inválido', severity: 'error' });
      return;
    }
    if (window.confirm('Tem certeza que deseja deletar este banner?')) {
      deleteBannerMutation.mutate(id);
    }
  };

  const handleReorder = (id, direction) => {
    if (!id || id === 'undefined') return;
    const banner = banners.find(b => (b.id === id || b._id === id));
    if (!banner) return;

    const currentOrder = banner.order_position ?? banner.orderPosition ?? 1;
    const newOrder = direction === 'up' ? Math.max(1, currentOrder - 1) : currentOrder + 1;
    reorderBannerMutation.mutate({ id, order: newOrder });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        setSnackbar({ open: true, message: 'Por favor, selecione apenas arquivos de imagem', severity: 'error' });
        return;
      }
      
      // Validar tamanho do arquivo (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({ open: true, message: 'O arquivo deve ter no máximo 5MB', severity: 'error' });
        return;
      }
      
      setSelectedFile(file);
      
      // Criar preview da imagem
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Limpar campo de URL da imagem quando um arquivo é selecionado
      setFormData({ ...formData, image_url: '' });
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusLabel = (isActive) => {
    return isActive ? 'Ativo' : 'Inativo';
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#FF6B35' }}>
              Gerenciar Banners
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: '#FF6B35',
                '&:hover': { bgcolor: '#FF8A65' }
              }}
            >
              Adicionar Banner
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Imagem</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Link</TableCell>
                  <TableCell>Ordem</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {banners
                  .sort((a, b) => (a.order_position ?? a.orderPosition ?? 0) - (b.order_position ?? b.orderPosition ?? 0))
                  .map((banner, index) => {
                    const bannerId = banner.id || banner._id;
                    const bannerImg = banner.image_url || banner.imageUrl;
                    const bannerLink = banner.link_url || banner.linkUrl;
                    const bannerOrder = banner.order_position ?? banner.orderPosition ?? (index + 1);
                    const bannerActive = banner.is_active !== undefined ? banner.is_active : (banner.isActive !== undefined ? banner.isActive : true);

                    return (
                      <TableRow key={bannerId || `banner-${index}`}>
                        <TableCell>
                          {bannerImg ? (
                            <Avatar
                              src={bannerImg}
                              variant="rounded"
                              sx={{ width: 80, height: 50 }}
                            >
                              <Image />
                            </Avatar>
                          ) : (
                            <Avatar variant="rounded" sx={{ width: 80, height: 50, bgcolor: '#FF6B35' }}>
                              <Image />
                            </Avatar>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {banner.title || 'Sem título'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            {banner.description ? (banner.description.length > 50 ? `${banner.description.substring(0, 50)}...` : banner.description) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {bannerLink ? (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#00D4FF', 
                                textDecoration: 'underline',
                                cursor: 'pointer'
                              }}
                              onClick={() => window.open(bannerLink, '_blank')}
                            >
                              Ver Link
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ opacity: 0.5 }}>
                              Sem link
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">
                              {bannerOrder}
                            </Typography>
                            <Box>
                              {bannerOrder > 1 ? (
                                <Tooltip title="Mover para cima">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleReorder(bannerId, 'up')}
                                    sx={{ color: '#00D4FF' }}
                                  >
                                    <ArrowUpward fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <IconButton
                                  size="small"
                                  disabled
                                  sx={{ color: 'rgba(255, 255, 255, 0.3)' }}
                                >
                                  <ArrowUpward fontSize="small" />
                                </IconButton>
                              )}
                              {bannerOrder < banners.length ? (
                                <Tooltip title="Mover para baixo">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleReorder(bannerId, 'down')}
                                    sx={{ color: '#00D4FF' }}
                                  >
                                    <ArrowDownward fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <IconButton
                                  size="small"
                                  disabled
                                  sx={{ color: 'rgba(255, 255, 255, 0.3)' }}
                                >
                                  <ArrowDownward fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(bannerActive)}
                            color={getStatusColor(bannerActive)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(banner)}
                                sx={{ color: '#00D4FF' }}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Deletar">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(bannerId)}
                                sx={{ color: '#FF1744' }}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog para Editar/Criar Banner */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingBanner ? 'Editar Banner' : 'Adicionar Banner'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Título do Banner"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              margin="normal"
            />
            
            {/* Upload de Arquivo */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#FF6B35' }}>
                Imagem do Banner
              </Typography>
              
              {/* Preview da Imagem */}
              {previewUrl && (
                <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '120px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '2px solid #FF6B35'
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleRemoveFile}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: '#FF1744',
                      color: 'white',
                      '&:hover': { bgcolor: '#D50000' }
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )}
              
              {/* Botão de Upload */}
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="banner-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="banner-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  sx={{
                    borderColor: '#FF6B35',
                    color: '#FF6B35',
                    '&:hover': {
                      borderColor: '#FF8A65',
                      bgcolor: 'rgba(255, 107, 53, 0.1)'
                    }
                  }}
                >
                  {selectedFile ? 'Alterar Imagem' : 'Selecionar Imagem'}
                </Button>
              </label>
              
              <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 5MB
              </Typography>
            </Box>
            
            {/* Campo de URL (alternativa ao upload) */}
            <TextField
              fullWidth
              label="OU URL da Imagem"
              value={formData.image_url}
              onChange={(e) => {
                setFormData({ ...formData, image_url: e.target.value });
                if (e.target.value) {
                  setSelectedFile(null);
                  setPreviewUrl(e.target.value);
                }
              }}
              margin="normal"
              placeholder="https://exemplo.com/imagem.jpg"
              disabled={!!selectedFile}
              helperText={selectedFile ? "Desmarque o arquivo selecionado para usar URL" : "Use este campo se preferir inserir uma URL"}
            />
            <TextField
              fullWidth
              label="URL do Link (opcional)"
              value={formData.link_url}
              onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
              margin="normal"
              placeholder="https://exemplo.com"
            />
            <TextField
              fullWidth
              label="Descrição"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Posição na Ordem"
              type="number"
              value={formData.order_position}
              onChange={(e) => setFormData({ ...formData, order_position: parseInt(e.target.value) || 1 })}
              margin="normal"
              inputProps={{ min: 1, max: banners.length + 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Banner Ativo"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createBannerMutation.isPending || updateBannerMutation.isPending}
            sx={{ bgcolor: '#FF6B35', '&:hover': { bgcolor: '#FF8A65' } }}
          >
            {(createBannerMutation.isPending || updateBannerMutation.isPending) ? 
              <CircularProgress size={20} /> : 'Salvar'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BannerManagement;
